import { useNavigate } from "react-router";
import { Button } from "../ui/button";
import { ArrowLeft, Lock, AlertCircle, Clock } from "lucide-react";
import { Alert, AlertDescription } from "../ui/alert";
import { useAuthUser } from "@/context/auth/AuthContext";
import VerPlanes from "./VerPlanes";

interface Props {
  reason?:
    | "plan_feature"
    | "plan_status"
    | "no_plan"
    | "trial_expired"
    | "no_features";
  requiredFeature?: string;
  isInTrial?: boolean;
}

/**
 * Componente que se muestra cuando el usuario no tiene acceso a una ruta
 * debido a limitaciones de su plan de negocio.
 *
 * El plan lo maneja el dueño: a un empleado no se le pide que "actualice su
 * plan" ni se le ofrece "Ver planes", que lo llevaba a una pantalla que no
 * puede abrir (trd-client#269, AC-7a). Se le dice que hable con el
 * administrador.
 */
function NoAccessPlan({ reason = "plan_feature", isInTrial = false }: Props) {
  const navigate = useNavigate();
  const esDueno = useAuthUser()?.type === "owner";

  const getMessage = () => {
    switch (reason) {
      case "trial_expired":
        return {
          title: "Período de prueba finalizado",
          description: esDueno
            ? "Tu período de prueba de 14 días ha expirado. Para continuar usando esta funcionalidad, necesitas activar o renovar tu plan."
            : "Terminó el período de prueba del negocio. Hablá con el administrador para que active el plan.",
          icon: <Clock className="h-5 w-5" />,
          variant: "destructive" as const,
          showPlansButton: true,
        };

      case "plan_status":
        return {
          title: "Plan inactivo",
          description: esDueno
            ? "Tu plan actual no está activo. Por favor, contacta al administrador o renueva tu suscripción para continuar usando el sistema."
            : "El plan del negocio no está activo. Hablá con el administrador.",
          icon: <AlertCircle className="h-5 w-5" />,
          variant: "destructive" as const,
          showPlansButton: true,
        };

      case "no_plan":
        return {
          title: "Sin plan asignado",
          description:
            "No tienes un plan asignado a tu negocio. Por favor, contacta al administrador para asignar un plan.",
          icon: <AlertCircle className="h-5 w-5" />,
          variant: "destructive" as const,
          showPlansButton: false,
        };

      case "no_features":
        return {
          title: "Plan sin funcionalidades",
          description:
            "Tu plan actual no tiene funcionalidades asignadas. Por favor, contacta al administrador.",
          icon: <AlertCircle className="h-5 w-5" />,
          variant: "destructive" as const,
          showPlansButton: false,
        };

      case "plan_feature":
      default:
        return {
          title: "Módulo no incluido en tu plan",
          description: esDueno
            ? "Esta funcionalidad no está incluida en tu plan actual. Actualiza tu plan para acceder a este módulo y muchas más funcionalidades."
            : "Esta funcionalidad no está incluida en el plan del negocio. Si la necesitás, hablá con el administrador.",
          icon: <Lock className="h-5 w-5" />,
          variant: "warning" as const,
          showPlansButton: true,
        };
    }
  };

  const message = getMessage();

  return (
    <div className="h-full flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full space-y-4">
        <Alert variant={message.variant}>
          {message.icon}
          <AlertDescription>
            <strong className="block mb-2">{message.title}</strong>
            <p className="text-sm">{message.description}</p>
            {isInTrial && (
              <p className="mt-3 text-xs font-medium text-alert-warning-foreground">
                🎉 Tienes 14 días gratis para probar tu plan
              </p>
            )}
          </AlertDescription>
        </Alert>

        <div className="flex gap-2">
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="w-full"
          >
            <ArrowLeft className="icon" />
            Volver al inicio
          </Button>

          {message.showPlansButton && (
            <VerPlanes variant="default" className="w-full" />
          )}
        </div>
      </div>
    </div>
  );
}

export default NoAccessPlan;
